<!-- @catalog Primary button primitive — all buttons build on this. See also CButtonIcon, CButtonText, CButtonMenu. -->
<template>
  <q-btn
    unelevated
    color="green"
    v-bind="filteredAttrs"
    :class="buttonClasses"
    :style="iconColorStyle"
    :size
    :data-font-size="fontSize"
    :icon="showMobileIcon ? mobileIcon : (attrs.icon as string | undefined)"
  >
    <template v-for="(_, slotName) in $slots" v-slot:[slotName]="scope">
      <slot v-if="!showMobileIcon" :name="slotName" v-bind="scope || {}" />
    </template>

    <c-tooltip v-if="tooltip.length">{{ tooltip }}</c-tooltip>
  </q-btn>
</template>

<script setup lang="ts">
import { computed, useAttrs } from "vue";
import { useQuasar } from "quasar";

const $q = useQuasar();
const attrs = useAttrs();

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(
  defineProps<{
    animateIcon?: boolean;
    // Only use SM for size. Where is the default is MD.
    size?: "14px" | "16px" | "sm" | "md";
    tooltip?: string;
    glass?: boolean;
    fontSize?: string | null;
    mobileIcon?: string;
    iconColor?: string;
  }>(),
  {
    animateIcon: false,
    size: "md",
    tooltip: "",
    glass: false,
    fontSize: null,
    mobileIcon: undefined,
    iconColor: undefined,
  },
);

const showMobileIcon = computed(() => props.mobileIcon && $q.screen.lt.sm);

const filteredAttrs = computed(() => {
  // Only filter label when mobileIcon is set and screen is mobile
  if (!showMobileIcon.value) {
    return attrs;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { label, ...rest } = attrs;

  return rest;
});

const iconColorStyle = computed(() => {
  if (!props.iconColor) {
    return undefined;
  }

  return { "--c-btn-icon-color": props.iconColor };
});

const buttonClasses = computed(() => {
  const { glass, animateIcon, size } = props;

  return {
    "c-btn--default-height": size === "md",
    "glass-effect": glass,
    "animate-icon": animateIcon,
  };
});
</script>

<style lang="scss">
body.desktop {
  .q-btn.q-focusable {
    .q-focus-helper {
      @apply tw-opacity-0;
    }
  }
}

button.q-btn,
a.q-btn {
  @apply tw-max-w-full tw-whitespace-nowrap tw-font-medium;

  &.c-btn--default-height {
    @apply tw-h-[40px];
  }
  text-transform: none !important;

  .q-btn__content {
    @apply tw-flex-nowrap;

    > span {
      @apply tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap;
    }
  }

  &.glass-effect {
    @apply tw-overflow-hidden;

    &::before {
      @apply tw-border-white/40 tw-bg-white/10 tw-backdrop-blur;
    }
  }

  // Outline buttons default to a solid white background.
  // Quasar's default is transparent — overridden here so outline buttons
  // sit cleanly on top of any page background. Glass-effect outline
  // buttons are intentionally excluded so the backdrop-blur tint can show
  // through (e.g. the portfolio banner upload action).
  &.q-btn--outline:not(.glass-effect) {
    background: white !important;

    // For the design-system default (green) outline, also darken the text.
    &.text-green {
      color: rgb(var(--color-grey-900)) !important;
    }
  }

  &.animate-icon .q-btn__content {
    > .q-icon {
      @apply tw-transition tw-duration-300;
    }
  }

  &.disabled {
    pointer-events: auto !important;
  }

  &.bg-green:not(.q-btn--outline) {
    box-shadow: var(--shadow-xxsmall);
  }

  &.bg-primary {
    --fa-primary-color: white;
    --fa-secondary-color: white;
    --fa-primary-rgba-color: rgba(255, 255, 255, 1);
    --fa-secondary-rgba-color: rgba(255, 255, 255, 0.4);
  }

  .q-icon {
    @apply tw-text-base;
    color: var(--c-btn-icon-color, inherit);
  }

  &--outline {
    &:before {
      border: 1px solid $grey;
    }
  }

  &::before {
    content: "";
    @apply tw-absolute tw-inset-0 tw-bg-white/0 tw-transition-colors;
  }

  &.q-btn--outline:not(.glass-effect) {
    &::before {
      border-color: theme("colors.gray.200") !important;
    }
  }

  &:hover::before {
    @apply tw-bg-gray-900/10;

    &.q-btn--outline {
      @apply tw-bg-black/5;
    }
  }
}
</style>
